import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { publicClient } from "@/lib/apiClient";

const VISITOR_KEY = "br_vid";
const SESSION_KEY = "br_sid";

// Paths we never want to count. Admin/api paths are also filtered server-side.
const EXCLUDE_PATTERNS = [/^\/admin(\/|$)/];

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    try { return crypto.randomUUID(); } catch (_) { /* fall through */ }
  }
  // Fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getOrCreate(storage, key) {
  try {
    let v = storage.getItem(key);
    if (!v) {
      v = uuid();
      storage.setItem(key, v);
    }
    return v;
  } catch (_) {
    // storage unavailable (private mode, etc.) — generate ephemeral id
    return uuid();
  }
}

function detectDevice() {
  if (typeof window === "undefined") return { device_type: "desktop", screen_width: 0 };
  const w = window.innerWidth || 0;
  let device_type = "desktop";
  if (w > 0 && w < 640) device_type = "mobile";
  else if (w >= 640 && w < 1024) device_type = "tablet";
  return { device_type, screen_width: w };
}

/**
 * Anonymous, first-party analytics tracker.
 * - visitor_id: random uuid persisted in localStorage (no PII).
 * - session_id: random uuid persisted in sessionStorage (resets per tab close).
 * - Skipped automatically for excluded paths and during admin sessions.
 */
export default function useAnalyticsTracker() {
  const { pathname } = useLocation();
  const lastSentRef = useRef("");

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    // Skip admin/excluded routes
    if (EXCLUDE_PATTERNS.some((re) => re.test(pathname))) return undefined;

    // De-dupe identical path within the same render cycle
    if (lastSentRef.current === pathname) return undefined;
    lastSentRef.current = pathname;

    const visitor_id = getOrCreate(window.localStorage, VISITOR_KEY);
    const session_id = getOrCreate(window.sessionStorage, SESSION_KEY);
    const { device_type, screen_width } = detectDevice();
    const referrer = document.referrer || "";

    // Fire-and-forget; failure must not impact UX
    publicClient
      .post("/analytics/track", {
        visitor_id,
        session_id,
        path: pathname,
        referrer,
        device_type,
        screen_width,
      })
      .catch(() => { /* swallow */ });

    return undefined;
  }, [pathname]);
}
